import { useCallback, useState } from "react";
import { ChevronRight, ExternalLink, MapPin } from "lucide-react";
import ScrollReveal from "@/components/motion/ScrollReveal";
import { cn } from "@/lib/utils";
import {
    ENVIRONMENT_MAP_BOUNDS,
    ENVIRONMENT_SITES,
} from "@/data/environment";

function latLngToPercent(lat, lng) {
    const { south, north, west, east } = ENVIRONMENT_MAP_BOUNDS;
    return {
        left: `${((lng - west) / (east - west)) * 100}%`,
        top: `${((north - lat) / (north - south)) * 100}%`,
    };
}

const STATUS_LABELS = {
    active: "Active project",
    partnered: "Partnered restoration",
    planned: "Probable site",
};

const STATUS_STYLES = {
    active: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40",
    partnered: "bg-amber-500/15 text-amber-200 border-amber-400/35",
    planned: "bg-zinc-500/20 text-zinc-300 border-zinc-400/30",
};

const MARKER_STYLES = {
    active: {
        pin: "bg-emerald-500",
        ring: "ring-emerald-400/60",
    },
    partnered: {
        pin: "bg-amber-500",
        ring: "ring-amber-400/60",
    },
    planned: {
        pin: "bg-zinc-400",
        ring: "ring-zinc-400/60",
    },
};

function SiteCard({ site, isSelected }) {
    return (
        <article
            id={`site-card-${site.id}`}
            role="tabpanel"
            aria-hidden={!isSelected}
            className={cn(
                "rounded-2xl border p-5 sm:p-6 transition-all duration-300",
                isSelected
                    ? "border-emerald-400/50 bg-emerald-500/10 ring-2 ring-emerald-400/40 shadow-lg shadow-emerald-950/30 scale-[1.01]"
                    : "border-white/10 bg-white/5 opacity-55 scale-[0.99]"
            )}
        >
            <span
                className={`inline-flex text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border mb-3 ${STATUS_STYLES[site.status]}`}
            >
                {STATUS_LABELS[site.status]}
            </span>
            <h3 className="font-display text-lg sm:text-xl font-bold text-white">
                {site.name}
            </h3>
            <p className="mt-1 text-sm text-white/55">{site.region}</p>
            <p className="mt-3 text-sm text-white/80 leading-relaxed font-medium">
                {site.summary}
            </p>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">
                {site.description}
            </p>
            <a
                href={site.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors"
                onClick={(e) => e.stopPropagation()}
            >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in maps
            </a>
        </article>
    );
}

export default function EnvironmentMap() {
    const [selectedId, setSelectedId] = useState(ENVIRONMENT_SITES[0].id);

    const selectSite = useCallback((id) => {
        setSelectedId(id);
        const card = document.getElementById(`site-card-${id}`);
        card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, []);

    const { west, south, east, north } = ENVIRONMENT_MAP_BOUNDS;
    const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${west}%2C${south}%2C${east}%2C${north}&layer=mapnik`;

    return (
        <div
            className="grid grid-cols-1 xl:grid-cols-12 gap-5 sm:gap-6"
            role="tablist"
            aria-label="Restoration sites"
        >
            <aside className="xl:col-span-3 flex flex-col gap-2 sm:gap-3">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/45 mb-1 px-1">
                    Select a site
                </p>
                {ENVIRONMENT_SITES.map((site) => {
                    const isSelected = selectedId === site.id;
                    const marker = MARKER_STYLES[site.status];
                    return (
                        <button
                            key={site.id}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            aria-controls={`site-card-${site.id}`}
                            onClick={() => selectSite(site.id)}
                            className={cn(
                                "group w-full text-left rounded-xl border px-4 py-3.5 sm:px-5 sm:py-4 transition-all duration-200",
                                isSelected
                                    ? "border-emerald-400/50 bg-emerald-500/10 ring-1 ring-emerald-400/30"
                                    : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/[0.08]"
                            )}
                        >
                            <div className="flex items-start gap-3">
                                <span
                                    className={cn(
                                        "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white shadow-md",
                                        marker.pin,
                                        isSelected && `ring-2 ${marker.ring}`
                                    )}
                                >
                                    <MapPin className="h-3.5 w-3.5 text-white" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-display text-sm sm:text-base font-bold text-white leading-tight">
                                        {site.name}
                                    </p>
                                    <p className="mt-1 text-xs text-white/50 line-clamp-2">
                                        {site.summary}
                                    </p>
                                </div>
                                <ChevronRight
                                    className={cn(
                                        "h-4 w-4 shrink-0 text-white/30 transition-transform mt-1",
                                        isSelected &&
                                            "text-emerald-400 translate-x-0.5"
                                    )}
                                />
                            </div>
                        </button>
                    );
                })}
            </aside>

            <ScrollReveal
                variant="scaleIn"
                className="xl:col-span-5 relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 h-[min(320px,50vh)] sm:h-[380px] xl:h-auto xl:min-h-[420px]"
            >
                <iframe
                    title="Restoration sites in Nakuru and Baringo"
                    src={embedUrl}
                    className="absolute inset-0 h-full w-full border-0 grayscale-[10%] contrast-[1.05]"
                    loading="lazy"
                />
                <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-zinc-950/15"
                    aria-hidden
                />
                <div className="absolute inset-0">
                    {ENVIRONMENT_SITES.map((site) => {
                        const pos = latLngToPercent(site.lat, site.lng);
                        const isSelected = selectedId === site.id;
                        const marker = MARKER_STYLES[site.status];
                        return (
                            <button
                                key={site.id}
                                type="button"
                                aria-label={`Select ${site.name}`}
                                aria-pressed={isSelected}
                                onClick={() => selectSite(site.id)}
                                className="absolute -translate-x-1/2 -translate-y-1/2 p-2"
                                style={{ left: pos.left, top: pos.top }}
                            >
                                <span
                                    className={cn(
                                        "flex h-10 w-10 items-center justify-center rounded-full border-2 border-white shadow-lg transition-transform duration-200",
                                        marker.pin,
                                        isSelected
                                            ? `scale-125 ring-4 ${marker.ring}`
                                            : "scale-100 opacity-90 hover:scale-110"
                                    )}
                                >
                                    <MapPin className="h-4 w-4 text-white" />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </ScrollReveal>

            <div className="xl:col-span-4 flex flex-col gap-4">
                {ENVIRONMENT_SITES.map((site) => (
                    <SiteCard
                        key={site.id}
                        site={site}
                        isSelected={selectedId === site.id}
                    />
                ))}
            </div>
        </div>
    );
}

